import map from "lodash/map"
import React, { useEffect, useRef, useState } from "react"
import { ReactSVG } from "react-svg"
import Slider from "react-slick"
import Box from "component-lib/Base/components/Box"
import { Typography } from "component-lib/Base/components/Typography/Typography"
import { makeStyles } from "tss-react/mui"
import { Button, FormattedTextField } from "component-lib/Base"
import {
  BOOTSTRAP_GRID_SM_MAX,
  BOOTSTRAP_GRID_MD_MAX,
  BOOTSTRAP_GRID_LG_MAX,
  BOOTSTRAP_GRID_IPAD_PRO_MAX,
} from "constants-lib/dimensions"
import { withAuthentication } from "hoc-lib/appAuthentication"
import getContentUrl from "utils-lib/getContentUrl"
import "./ReviewCarousel.scss"
import { getWindow } from "utils-lib/getWindow"
import { getZoomLevel } from "utils-lib/getZoomLevel/getZoomLevel"

const ssrWindow = getWindow()

const useStyles = makeStyles()(
  (theme, { colorPalette, addCardShadow, outlinedArrows, margin, width }) => ({
    root: {
      "& .slick-arrow.slick-prev:before, & .slick-arrow.slick-next:before": {
        display: `none !important`,
      },
      "& .slick-slider .prev, .slick-slider .next": {
        display: `none !important`,
        [theme.breakpoints.up(`lg`)]: {
          display: `block !important`,
        },
      },
      "& .slick-prev": {
        "&:focus": {
          outline: `2px auto -webkit-focus-ring-color !important`,
        },
      },
      "& .slick-next": {
        "&:focus": {
          outline: `2px auto -webkit-focus-ring-color !important`,
        },
      },
      "& .slick-track": {
        display: `flex !important`,
      },
      "& .slick-slide": {
        height: `inherit !important`,
        width: `554px !important`,
        margin,
      },
      "& .slick-slide > div": {
        height: `100% !important`,
      },
      "& .slick-slide > div > div": {
        height: `100% !important`,
        width: ` 100% !important`,
        [theme.breakpoints.down(`sm`)]: {
          width: ` 105% !important`,
        },
      },
      "& .slick-dots button:focus": {
        outline: "2px auto -webkit-focus-ring-color",
      },
    },
    carouselBackground: {
      [theme.breakpoints.up(`lg`)]: {
        padding: `0 4rem`,
      },
      background:
        colorPalette === "white"
          ? theme.palette.common.white
          : theme.palette.background.sand,
    },
    carouselItem: {
      display: `flex`,
      height: `100%`,
      width: `100%`,
      padding: `0 2rem`,
      [theme.breakpoints.up(`xl`)]: {
        height: `16rem`,
        width: `35.5rem !important`,
      },
    },
    itemContainer: {
      display: `flex`,
      padding: `1.4rem 2rem 2rem`,
      borderRadius: `4px`,
      height: `calc(100% - 1rem)`,
      boxShadow: `${
        addCardShadow
          ? `0 2px 8px 0 ${theme.palette.background.dropShadow}`
          : "none"
      }`,
      margin: addCardShadow ? `0.25rem 0` : `auto`,
      background: theme.palette.common.white,
      width,
      [theme.breakpoints.down(`md`)]: {
        minHeight: `234px`,
        padding: `1rem 2rem`,
      },
      [theme.breakpoints.down(`sm`)]: {
        padding: `1.875rem 1rem`,
        minHeight: `360px`,
      },
    },
    icon: {
      "&>svg": {
        width: `100%`,
        height: `100%`,
      },
      display: `flex`,
      justifyContent: `center`,
      alignItems: `center`,
      padding: `0.7rem`,
      width: `75px`,
      height: `75px`,
      minWidth: `75px`,
      minHeight: `75px`,
      background: theme.palette.common.white,
      borderRadius: `100%`,
      marginLeft: `-3.75rem`,
      marginRight: `1rem`,
      boxShadow: `0 2px 8px 0 ${theme.palette.background.dropShadow}`,
      [theme.breakpoints.down(`sm`)]: {
        padding: `1rem`,
        marginLeft: `-2.75rem`,
      },
    },
    rating: {
      display: `flex`,
      justifyContent: `space-between`,
      width: `5.25rem`,
      marginRight: `0.5rem`,
      "& .star": {
        width: `13px`,
        "&>svg": {
          height: `13px`,
        },
      },
    },
    ratingWrapper: {
      margin: `0.25rem 0`,
      flexWrap: `wrap`,
      flexDirection: `row`,
    },
    sourceWrapper: {
      display: `flex`,
      [theme.breakpoints.down(`sm`)]: {
        width: `100%`,
      },
    },
    arrowWrapper: {
      display: `flex`,
      justifyContent: `space-between`,
      [theme.breakpoints.up(`xl`)]: {
        display: `none`,
      },
    },
    arrow: {
      height: `3.125rem`,
      width: `3.125rem`,
      borderRadius: `100%`,
      [theme.breakpoints.up(`lg`)]: outlinedArrows
        ? {
            "&>svg": {
              height: `3.125rem`,
              width: `3.125rem`,
            },
            "&:hover": {
              boxShadow: `0 2px 8px 0 ${theme.palette.background.dropShadow}`,
            },
          }
        : {
            "&>svg": {
              height: `3.125rem`,
              width: `3.125rem`,
            },
            "&:hover": {
              "&>svg>path": {
                fill: theme.palette.button.primaryHover,
              },
            },
          },
      "&>svg": {
        width: `100%`,
        maxHeight: `100%`,
        height: `auto`,
        objectFit: `fill`,
      },
    },
    dots: {
      bottom: `0px !important`,
      [theme.breakpoints.down(`md`)]: {
        bottom: `-44px !important`,
      },
      display: `flex !important`,
      justifyContent: `center !important`,
      left: `12.5% !important`,
      width: `75% !important`,
      "& li>button": {
        height: `.75rem !important`,
        width: `0.75rem !important`,
        "&::before": {
          height: `12px !important`,
          width: `12px !important`,
          borderRadius: `11px !important`,
          color: theme.palette.button.primary,
          opacity: 1,
        },
        "&:hover": {
          "&::before": {
            color: theme.palette.primary.pureBlack,
          },
        },
      },
      "& .slick-active>button": {
        boxSizing: `border-box !important`,
        height: `12px !important`,
        width: `12px !important`,
        border: `2px solid #024731 !important`,
        borderRadius: `11px !important`,
      },
    },
  }),
)

export const ReviewCarousel = ({
  cardView = false,
  componentBackgroundColor = "",
  reviewCarouselList = [],
  addCardShadow = true,
  colorPalette = "",
}) => {
  const sliderRef = useRef(null)
  const cardRefs = useRef({})

  const onNextClick = () => sliderRef.current.slickNext()
  const onPrevClick = () => sliderRef.current.slickPrev()
  const [margin, setMargin] = useState("0 1rem")
  const [width, setWidth] = useState("100%")

  const { classes } = useStyles({
    colorPalette,
    addCardShadow,
    cardView,
    outlinedArrows: componentBackgroundColor === `white`,
    margin,
    width,
  })

  const handleZoom = () => {
    // Get the zoomLevel of the page
    const zoomLevel = getZoomLevel()

    // Check if the zoom level is greater than 3
    const isZoomLevelHigh = zoomLevel > 3

    // Check if the zoom level is less than 5
    const isZoomLevelLow = zoomLevel < 5

    // Ensure the zoom level is not exactly 3.5
    const isZoomLevelNotSpecific = zoomLevel !== 3.5

    // If the zoom level is between 3 and 5, but not exactly 3.5
    if (isZoomLevelHigh && isZoomLevelLow && isZoomLevelNotSpecific) {
      setMargin("0 8rem")
      setWidth("150%")
    } else {
      setMargin("0 1rem")
      setWidth("100%")
    }
  }

  useEffect(() => {
    ssrWindow.addEventListener("resize", handleZoom)
    handleZoom()
    return () => {
      ssrWindow.removeEventListener("resize", handleZoom)
    }
  }, [])

  function renderArrow(direction, onClick) {
    return (
      <Button
        analytics_key={`ReviewCarousel ${direction}`}
        className={`review-carousel-${direction}`}
        onClick={onClick}
        ariaLabel={direction === `next` ? `next Slide` : `previous Slide`}
      >
        <ReactSVG
          wrapper="div"
          className={`${classes.arrow} ${direction}`}
          src={getContentUrl(
            `/content/dam/wm/icons/controls/20x20/green/${
              ssrWindow.innerWidth < BOOTSTRAP_GRID_IPAD_PRO_MAX
                ? ``
                : `solid-circle-`
            }arrow-${direction === `next` ? `right` : `left`}.svg`,
          )}
        />
      </Button>
    )
  }

  function renderCarouselItems() {
    return map(reviewCarouselList, (item, index) => {
      const {
        heading,
        reviewStars,
        source,
        name,
        image,
        imageAltText,
        bodyText,
      } = item
      const starStack = []

      for (let i = 0; i < 5; i += 1) {
        starStack.push(i < reviewStars)
      }

      return (
        <div
          key={`${classes.carouselItem}-${index}`}
          className={classes.carouselItem}
        >
          <div
            // eslint-disable-next-line no-return-assign
            ref={(ref) => (cardRefs.current[index] = ref)}
            className={`${classes.itemContainer}`}
          >
            <ReactSVG
              className={classes.icon}
              wrapper="div"
              src={getContentUrl(image)}
              alt={imageAltText}
            />
            <Box display="flex" flexDirection="column">
              <Typography variant="h4" className="box-heading">
                {heading}
              </Typography>
              <Box display="flex" className={classes.ratingWrapper}>
                <Box className={classes.sourceWrapper}>
                  <Box
                    className={classes.rating}
                    role="img"
                    aria-label={`${starStack.length} star rating`}
                  >
                    {map(starStack, (star) => (
                      <ReactSVG
                        className="star"
                        wrapper="span"
                        src={getContentUrl(
                          star
                            ? "/content/dam/wm/icons/graphics/star-filled.svg"
                            : "/content/dam/wm/icons/graphics/star-outlined.svg",
                        )}
                      />
                    ))}
                  </Box>
                  <Typography variant="body1">
                    {" "}
                    <div>{source.split("-")[0].trim()}</div>{" "}
                  </Typography>
                </Box>
                <div className="rich-text">
                  {`${
                    ssrWindow.innerWidth >= BOOTSTRAP_GRID_IPAD_PRO_MAX
                      ? " - "
                      : ""
                  }`}
                  {source.split("-")[1] && source.split("-")[1].trim()}
                </div>
                {name && (
                  <>
                    <Typography
                      className="d-none d-md-block mx-0 mx-md-1"
                      variant="body1"
                    >
                      -
                    </Typography>
                    <Typography variant="body1">{name}</Typography>
                  </>
                )}
              </Box>
              <FormattedTextField>{bodyText}</FormattedTextField>
            </Box>
          </div>
        </div>
      )
    })
  }
  return renderCarouselItems().length !== 0 ? (
    <div
      data-testid="ReviewCarousel"
      className={`${
        renderCarouselItems().length === 2
          ? "RatingsReviewCarousel"
          : "ReviewCarousel"
      } ${classes.root} ${classes.carouselBackground}`}
    >
      <div className="container p-0">
        <Slider
          ref={sliderRef}
          dots
          appendDots={(dots) => (
            <ul aria-label="Carousel Slides">
              {dots.map((item, index) => {
                const dotsClassName = item?.props?.className
                return (
                  <li className={dotsClassName} key={index}>
                    {React.cloneElement(item.props.children, {
                      "aria-pressed": dotsClassName === "slick-active",
                    })}
                  </li>
                )
              })}
            </ul>
          )}
          infinite
          slidesToShow={2}
          slidesToScroll={2}
          nextArrow={renderArrow(`next`, onNextClick)}
          prevArrow={renderArrow(`prev`, onPrevClick)}
          dotsClass={`slick-dots ${classes.dots}`}
          responsive={[
            {
              breakpoint: BOOTSTRAP_GRID_SM_MAX,
              settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
              },
            },
            {
              breakpoint: BOOTSTRAP_GRID_MD_MAX,
              settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                centerMode: true,
              },
            },
            {
              breakpoint: BOOTSTRAP_GRID_LG_MAX,
              settings: {
                slidesToShow: renderCarouselItems().length === 2 ? 1 : 2,
                slidesToScroll: 1,
              },
            },
          ]}
        >
          {renderCarouselItems()}
        </Slider>
        <Box
          className={`${classes.arrowWrapper} ${
            ssrWindow.innerWidth > BOOTSTRAP_GRID_IPAD_PRO_MAX
              ? "carouselIcon"
              : ""
          }`}
        >
          {renderArrow(`prev`, onPrevClick)}
          {renderArrow(`next`, onNextClick)}
        </Box>
      </div>
    </div>
  ) : null
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// ReviewCarousel.propTypes = {
//   /** use dropshadowed card styling */
//   cardView: PropTypes.bool,
//   /** component background color, used to determine if arrow styling */
//   componentBackgroundColor: PropTypes.string,
//   /** button styling */
//   reviewCarouselList: PropTypes.arrayOf(
//     PropTypes.shape({
//       /** heading text */
//       heading: PropTypes.string,
//       /** number of review stars path */
//       reviewStars: PropTypes.number,
//       /** original source of review (e.g. Google, Yelp, etc) key */
//       source: PropTypes.string,
//       /** name of submitter */
//       name: PropTypes.string,
//       /** logo path for source of review */
//       image: PropTypes.string,
//       /** alt text for logo */
//       imageAltText: PropTypes.string,
//       /** actual review text */
//       bodyText: PropTypes.string,
//     }),
//   ),
//   addCardShadow: PropTypes.bool,
//   colorPalette: PropTypes.string,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// ReviewCarousel.defaultProps = {
//   reviewCarouselList: [],
//   cardView: false,
//   addCardShadow: true,
//   componentBackgroundColor: "",
//   colorPalette: "",
// }

export default withAuthentication(ReviewCarousel)
